/**
 * Pills for the statuses that sit under an assignment's workflow state (idc-portal-design v1.1, AIC-178): a spec's
 * review status, a result's, an interview link's, a vendor contact's and a review item's (AIC-179). They aren't workflow states, so they don't
 * join the 12-state catalogue (./status.tsx) or the status key; they reuse its tones (whose move it is) and always
 * carry the full text label. A status this map doesn't know is shown as its text in the waiting tone, never blank.
 */
import type { Tone } from "./status.tsx";

const SUB: Readonly<Record<string, { label: string; tone: Tone }>> = {
  // interview spec (spec review)
  awaiting_approval: { label: "Awaiting approval", tone: "you" },
  approved: { label: "Approved", tone: "done" },
  rejected: { label: "Rejected", tone: "off" },
  superseded: { label: "Superseded", tone: "off" },
  // results
  awaiting_analyst: { label: "Awaiting review", tone: "you" },
  needs_follow_up: { label: "Needs follow-up", tone: "risk" },
  exported: { label: "Exported", tone: "done" },
  // interview links (sessions)
  issued: { label: "Issued", tone: "wait" },
  code_verified: { label: "Code verified", tone: "wait" },
  in_progress: { label: "In progress", tone: "wait" },
  review: { label: "In review", tone: "wait" },
  // AIC-252: the call or form ended part-way; the vendor can resume on the same link.
  partial: { label: "Partial", tone: "risk" },
  completed: { label: "Completed", tone: "done" },
  abandoned: { label: "Abandoned", tone: "off" },
  expired: { label: "Expired", tone: "off" },
  locked: { label: "Locked", tone: "risk" },
  // review items (AIC-179)
  open: { label: "Open", tone: "you" },
  resolved: { label: "Resolved", tone: "done" },
  dismissed: { label: "Dismissed", tone: "off" },
  // vendor contacts
  active: { label: "Active", tone: "done" },
  bounced: { label: "Bounced", tone: "risk" },
  retired: { label: "Retired", tone: "off" },
};

const humanize = (s: string) => s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/** Whether the status is one this map names (an unknown one is only worded, never trusted as a code). */
export const isSubStatus = (status: string): boolean => Object.hasOwn(SUB, status);

export function subStatus(status: string): { label: string; tone: Tone } {
  return SUB[status] ?? { label: humanize(status), tone: "wait" };
}

/** A text pill in the status tone: no icon (icons belong to the 12 workflow states). */
export function SubPill({ status }: { readonly status: string }) {
  const s = subStatus(status);
  return <span className={`ipd-pill ipd-t--${s.tone}`} data-sub={status}><span>{s.label}</span></span>;
}
